import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Users as UsersIcon,
  Shield,
  User,
  Loader2,
  Plus,
  X,
  Mail,
  UserIcon,
  Trash2,
  Pencil,
  Check,
  Power,
  PowerOff,
} from "lucide-react";
import {
  PAGE_GROUPS,
  PAGE_GROUP_KEYS,
  PAGE_ADMIN_GROUP_KEYS,
  type PageGroupKey,
  type PageAdminGroupKey,
} from "@/lib/pagePermissions";

interface UserRecord {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  pagePermissions: string[];
  pageAdminPermissions: string[];
  createdAt: string;
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function PagePermissionsEditor({
  user,
  onSave,
  onCancel,
  saving,
  canEditGroupAdmin,
}: {
  user: UserRecord;
  onSave: (keys: PageGroupKey[], adminKeys: PageAdminGroupKey[]) => void;
  onCancel: () => void;
  saving: boolean;
  // Per-group admin (e.g. "Admin of Expenses") can only be granted by the
  // real super-admin. For everyone else the section is hidden and the
  // existing list is preserved unchanged on save.
  canEditGroupAdmin: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(user.pagePermissions ?? []));
  const [adminSelected, setAdminSelected] = useState<Set<string>>(
    new Set(user.pageAdminPermissions ?? []),
  );
  const toggle = (k: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const toggleAdmin = (k: string) => {
    setAdminSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  return (
    <div className="col-span-12 mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold text-duffells-grey/70 uppercase tracking-wider mb-3">
        Pages this user can access
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {/* Email Log is always available and has no assignable permission. */}
        {PAGE_GROUPS.filter((g) => g.key !== "email_log").map((g) => {
          const Icon = g.icon;
          const checked = selected.has(g.key);
          return (
            <label
              key={g.key}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                checked
                  ? "border-primary bg-white"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(g.key)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon className="w-4 h-4 text-duffells-grey/70" />
                  <span className="text-sm font-medium text-duffells-grey">{g.label}</span>
                  {g.key === "expenses" && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-duffells-grey/70">
                      users can upload receipts only
                    </span>
                  )}
                </div>
                <p className="text-xs text-duffells-grey/60 mt-0.5">{g.description}</p>
              </div>
            </label>
          );
        })}
      </div>
      {(user.role === "admin" || user.role === "super_admin") && (
        <p className="text-xs text-duffells-grey/60 mt-3">
          Note: {user.role === "super_admin" ? "Super-admins" : "Admins"} see every page regardless of
          the boxes above — these only take effect if the user's role is changed to "User".
        </p>
      )}

      {canEditGroupAdmin && user.role !== "super_admin" && (
        <div className="mt-5 pt-4 border-t border-slate-200">
          <p className="text-xs font-semibold text-duffells-grey/70 uppercase tracking-wider mb-2">
            Per-group admin powers
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              Super-admin only
            </span>
          </p>
          <p className="text-xs text-duffells-grey/60 mb-3">
            Grants this user full admin powers <em>inside the selected page group only</em>
            {" "}— they can match, manage statements and templates, etc. — without making them an
            overall admin or giving them access to any other group.
            {user.role === "admin" && (
              <span className="block mt-1 text-amber-700">
                This user is already a global admin, so per-group admin is redundant for them.
              </span>
            )}
          </p>
          <div className="space-y-2">
            {PAGE_ADMIN_GROUP_KEYS.map((k) => {
              const group = PAGE_GROUPS.find((g) => g.key === k);
              if (!group) return null;
              const Icon = group.icon;
              const checked = adminSelected.has(k);
              return (
                <label
                  key={k}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    checked ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAdmin(k)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className="w-4 h-4 text-duffells-grey/70" />
                      <span className="text-sm font-medium text-duffells-grey">
                        Admin of {group.label}
                      </span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => {
            // Sanitize: only send canonical keys, dropping unknowns
            // (e.g. stale entries left in the DB from a previous schema).
            const clean = PAGE_GROUP_KEYS.filter((k) => selected.has(k));
            const cleanAdmin = PAGE_ADMIN_GROUP_KEYS.filter((k) => adminSelected.has(k));
            onSave(clean, cleanAdmin);
          }}
          disabled={saving}
          className="gap-2"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user, isRealSuperAdmin } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingPermsId, setEditingPermsId] = useState<number | null>(null);
  const [savingPermsId, setSavingPermsId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [togglingActiveId, setTogglingActiveId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("user");

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BASE}/api/users`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load users");
      const data = await res.json();
      setUsers(data);
    } catch {
      toast({ title: "Failed to load users", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleRole = async (targetUser: UserRecord) => {
    const newRoleValue = targetUser.role === "admin" ? "user" : "admin";
    setUpdatingId(targetUser.id);
    try {
      const res = await fetch(`${BASE}/api/users/${targetUser.id}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRoleValue }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update role");
      }
      toast({ title: `${targetUser.name} is now ${newRoleValue === "admin" ? "an admin" : "a user"}` });
      fetchUsers();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteUser = async (targetUser: UserRecord) => {
    if (!confirm(`Are you sure you want to delete ${targetUser.name} (${targetUser.email})? This cannot be undone.`)) {
      return;
    }
    setDeletingId(targetUser.id);
    try {
      const res = await fetch(`${BASE}/api/users/${targetUser.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to delete user");
      }
      toast({ title: `${targetUser.name} has been removed` });
      fetchUsers();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const savePermissions = async (
    targetUser: UserRecord,
    keys: PageGroupKey[],
    adminKeys: PageAdminGroupKey[],
  ) => {
    setSavingPermsId(targetUser.id);
    try {
      const res = await fetch(`${BASE}/api/users/${targetUser.id}/page-permissions`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pagePermissions: keys }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update page access");
      }
      // Only super-admin may PATCH /page-admin-permissions — and only
      // send when something actually changed, so we don't 403 a non-super
      // admin who happened to open the editor.
      const currentAdmin = new Set(targetUser.pageAdminPermissions ?? []);
      const nextAdmin = new Set(adminKeys);
      const adminChanged =
        currentAdmin.size !== nextAdmin.size ||
        [...nextAdmin].some((k) => !currentAdmin.has(k));
      if (isRealSuperAdmin && adminChanged) {
        const adminRes = await fetch(
          `${BASE}/api/users/${targetUser.id}/page-admin-permissions`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ pageAdminPermissions: adminKeys }),
          },
        );
        if (!adminRes.ok) {
          const err = await adminRes.json();
          throw new Error(err.error || "Failed to update group-admin access");
        }
      }
      toast({ title: `Page access updated for ${targetUser.name}` });
      setEditingPermsId(null);
      fetchUsers();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setSavingPermsId(null);
    }
  };

  const toggleActive = async (targetUser: UserRecord) => {
    const nextActive = !targetUser.isActive;
    setTogglingActiveId(targetUser.id);
    try {
      const res = await fetch(`${BASE}/api/users/${targetUser.id}/active`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ isActive: nextActive }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update status");
      }
      toast({
        title: `${targetUser.name} has been ${nextActive ? "reactivated" : "deactivated"}`,
      });
      fetchUsers();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setTogglingActiveId(null);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${BASE}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newName, email: newEmail, role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to send invitation");
      }
      toast({ title: `Invitation sent to ${newEmail}` });
      setNewName("");
      setNewEmail("");
      setNewRole("user");
      setShowCreateForm(false);
      fetchUsers();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
              <UsersIcon className="w-6 h-6 text-duffells-orange" />
              User Management
            </h1>
            <p className="text-duffells-grey/70 mt-1">
              Manage user accounts, roles, and which pages each user can access.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="gap-2">
              <a href={`${BASE}/email-log`}>
                <Mail className="w-4 h-4" />
                Email log
              </a>
            </Button>
            {user?.role === "super_admin" && (
              <Button asChild variant="outline" className="gap-2">
                <a href={`${BASE}/audit-log`}>
                  <Shield className="w-4 h-4" />
                  Audit log
                </a>
              </Button>
            )}
            <Button onClick={() => setShowCreateForm(!showCreateForm)} className="gap-2">
              {showCreateForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showCreateForm ? "Cancel" : "Add User"}
            </Button>
          </div>
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-duffells-grey mb-1">Invite New User</h3>
            <p className="text-sm text-duffells-grey/70 mb-4">
              An email invitation is sent via Clerk. The user sets their own password and
              joins automatically — their local profile here is created on first sign-in.
            </p>
            <form onSubmit={createUser} className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-duffells-grey">Full Name</label>
                <div className="relative">
                  <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-duffells-grey/40" />
                  <input
                    type="text"
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
                    placeholder="Jane Smith"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-duffells-grey">Email Address</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-duffells-grey/40" />
                  <input
                    type="email"
                    required
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
                    placeholder="jane@duffells.co.uk"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-duffells-grey">Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={creating} className="gap-2">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {creating ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <div className="grid grid-cols-12 text-xs font-semibold text-duffells-grey/70 uppercase tracking-wider gap-3">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-1">Role</div>
              <div className="col-span-3">Pages</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-duffells-orange" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-duffells-grey/50">No users found.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {users.map((u) => {
                const isElevated = u.role === "admin" || u.role === "super_admin";
                const perms = u.pagePermissions ?? [];
                const adminPerms = u.pageAdminPermissions ?? [];
                const labelFor = (k: string) =>
                  PAGE_GROUPS.find((g) => g.key === k)?.label ?? k;
                return (
                  <div
                    key={u.id}
                    className="px-6 py-4 grid grid-cols-12 items-center hover:bg-slate-50 transition-colors gap-3"
                  >
                    <div className="col-span-3">
                      <p className="font-medium text-duffells-grey flex items-center gap-2">
                        {u.name}
                        {!u.isActive && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            Deactivated
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm text-duffells-grey/70 truncate">{u.email}</p>
                    </div>
                    <div className="col-span-1">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          u.role === "super_admin"
                            ? "bg-amber-100 text-amber-700"
                            : u.role === "admin"
                            ? "bg-duffells-orange/10 text-duffells-orange"
                            : "bg-slate-100 text-duffells-grey"
                        }`}
                      >
                        {isElevated ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {u.role === "super_admin" ? "Super" : u.role === "admin" ? "Admin" : "User"}
                      </span>
                    </div>
                    <div className="col-span-3">
                      {isElevated ? (
                        <span
                          className="text-xs text-duffells-grey/60 italic"
                          title="Admins and super-admins see every page regardless of the page-access list."
                        >
                          Full access (role override)
                        </span>
                      ) : perms.length === 0 && adminPerms.length === 0 ? (
                        <span className="text-xs text-duffells-grey/50">No pages assigned</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {perms.map((p) => (
                            <span
                              key={p}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium"
                            >
                              {labelFor(p)}
                            </span>
                          ))}
                          {adminPerms.map((p) => (
                            <span
                              key={`admin-${p}`}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium inline-flex items-center gap-1"
                              title={`Admin powers in ${labelFor(p)}`}
                            >
                              <Shield className="w-2.5 h-2.5" />
                              {labelFor(p)} admin
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPermsId(editingPermsId === u.id ? null : u.id)}
                        className="text-xs gap-1"
                        title="Edit which pages this user can access"
                      >
                        <Pencil className="w-3 h-3" />
                        Pages
                      </Button>
                      {u.id === user?.id ? (
                        <span className="text-xs text-duffells-grey/40">You</span>
                      ) : u.role === "super_admin" ? (
                        <span
                          className="text-xs text-duffells-grey/40"
                          title="Super-admin role can only be changed in the database."
                        >
                          Protected
                        </span>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={updatingId === u.id}
                            onClick={() => toggleRole(u)}
                            className="text-xs"
                          >
                            {updatingId === u.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : u.role === "admin" ? (
                              "Make User"
                            ) : (
                              "Make Admin"
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={togglingActiveId === u.id}
                            onClick={() => toggleActive(u)}
                            className={`text-xs ${
                              u.isActive
                                ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                : "text-green-600 hover:text-green-700 hover:bg-green-50"
                            }`}
                            title={u.isActive ? "Deactivate (revokes access immediately)" : "Reactivate"}
                          >
                            {togglingActiveId === u.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : u.isActive ? (
                              <PowerOff className="w-3 h-3" />
                            ) : (
                              <Power className="w-3 h-3" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={deletingId === u.id}
                            onClick={() => deleteUser(u)}
                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            {deletingId === u.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                    {editingPermsId === u.id && (
                      <PagePermissionsEditor
                        user={u}
                        onSave={(keys, adminKeys) => savePermissions(u, keys, adminKeys)}
                        onCancel={() => setEditingPermsId(null)}
                        saving={savingPermsId === u.id}
                        canEditGroupAdmin={isRealSuperAdmin}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-duffells-grey mb-3">Role Permissions</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-amber-700" />
                <span className="font-medium text-duffells-grey">Super-Admin</span>
              </div>
              <p className="text-sm text-duffells-grey/70">
                Full access to every page including Cash Receipts. Role is locked — change in DB only.
              </p>
            </div>
            <div className="p-4 bg-duffells-orange/5 rounded-lg border border-duffells-orange/10">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-duffells-orange" />
                <span className="font-medium text-duffells-grey">Admin</span>
              </div>
              <p className="text-sm text-duffells-grey/70">
                Full access to every page except Cash Receipts (super-admin only). Page-access list
                is ignored.
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-duffells-grey" />
                <span className="font-medium text-duffells-grey">User</span>
              </div>
              <p className="text-sm text-duffells-grey/70">
                Only sees the pages ticked in their "Pages" list. The home grid and sidebar are
                filtered to match.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
